import { useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { useAuthStore } from '@/stores/auth.store'
import { referralService } from '@/services/referral.service'
import logoImg from '@/assets/logo-icon.png'

export function ReferralPage() {
  const { t } = useTranslation()
  const { token } = useAuthStore()
  const navigate = useNavigate()
  const [copied, setCopied] = useState(false)
  const qrRef = useRef<HTMLDivElement>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['referral'],
    queryFn: () => referralService.getReferral(token!),
    enabled: !!token,
  })

  const { data: stats } = useQuery({
    queryKey: ['referral-stats'],
    queryFn: () => referralService.getStats(token!),
    enabled: !!token,
  })

  const handleCopy = async () => {
    if (data?.referralUrl) {
      await navigator.clipboard.writeText(data.referralUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  if (isLoading) {
    return <div className="p-8 text-center">{t('common.loading')}</div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('referral.title')}
          </h1>
          <button
            onClick={() => navigate('/scout/dashboard')}
            className="text-gray-600 hover:text-gray-900"
          >
            &larr; {t('common.back')}
          </button>
        </div>

        {/* QR Code */}
        <div className="bg-white rounded-lg shadow p-8 mb-6 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {t('referral.qrCode')}
          </h2>
          <p className="text-gray-600 mb-6">{t('referral.qrCodeDescription')}</p>
          {data?.referralUrl && (
            <div ref={qrRef} className="inline-block bg-white p-4 border-2 border-gray-200 rounded-lg">
              <QRCodeSVG
                value={data.referralUrl}
                size={256}
                level="H"
                includeMargin
                imageSettings={{
                  src: logoImg,
                  x: undefined,
                  y: undefined,
                  height: 48,
                  width: 48,
                  excavate: true,
                }}
              />
            </div>
          )}
          {data?.referralUrl && (
            <div className="mt-4">
              <button
                onClick={() => {
                  const svg = qrRef.current?.querySelector('svg')
                  if (!svg) return
                  const svgData = new XMLSerializer().serializeToString(svg)
                  const blob = new Blob([svgData], { type: 'image/svg+xml' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = 'referral-qr-code.svg'
                  a.click()
                  URL.revokeObjectURL(url)
                }}
                className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                {t('referral.downloadQR')}
              </button>
            </div>
          )}
        </div>

        {/* Referral Link */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {t('referral.yourLink')}
          </h2>
          <div className="flex items-center gap-3">
            <div className="flex-1 px-4 py-3 bg-gray-50 rounded-lg font-mono text-sm text-gray-700 overflow-x-auto">
              {data?.referralUrl}
            </div>
            <button
              onClick={handleCopy}
              className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              {copied ? t('referral.copied') : t('referral.copy')}
            </button>
          </div>
          <div className="mt-3 text-sm text-gray-500">
            {t('referral.referralCode')}: <span className="font-mono font-semibold">{data?.referralCode}</span>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">{t('referral.totalClicks')}</div>
              <div className="text-2xl font-bold text-blue-600">{stats.stats?.totalClicks || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">{t('referral.conversions')}</div>
              <div className="text-2xl font-bold text-green-600">{stats.stats?.conversions || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">{t('referral.conversionRate')}</div>
              <div className="text-2xl font-bold text-purple-600">{(stats.stats?.conversionRate || 0).toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-600">{t('referral.merchants')}</div>
              <div className="text-2xl font-bold text-orange-600">{stats.stats?.merchants || 0}</div>
            </div>
          </div>
        )}

        {/* How to use */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            {t('referral.howToUse')}
          </h2>
          <ol className="space-y-2 text-gray-700 list-decimal list-inside">
            <li>{t('referral.instruction1')}</li>
            <li>{t('referral.instruction2')}</li>
            <li>{t('referral.instruction3')}</li>
            <li>{t('referral.instruction4')}</li>
          </ol>
        </div>
      </div>
    </div>
  )
}
